'use client';

import React, { createContext, useContext, useReducer, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Tool,
  Color,
  ModelType,
  Layer,
  LayerViewMode,
  BodyPart,
  BrushSize,
  Point,
  ShapePreview,
} from '@/app/lib/skinEditor/types';
import {
  type SkinLayer,
  compositeDocument,
  compositeForPreview,
  getActiveLayer,
  isBlankDocument,
} from '@/app/lib/skinEditor/layers';
import { readPixel, type Bounds } from '@/app/lib/skinEditor/paint';
import { AUTOSAVE_DEBOUNCE_MS, readAutosave, writeAutosave } from '@/app/lib/skinEditor/autosave';
import { type EditorState, type EditorAction, editorReducer, initialState } from '@/app/lib/skinEditor/editorState';

export type { EditorState, EditorAction };

// ---------------------------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------------------------

interface SkinEditorContextValue {
  state: EditorState;
  dispatch: React.Dispatch<EditorAction>;
  /** The flattened skin (what is downloaded/saved/previewed). Null only during SSR. */
  texture: ImageData | null;
  /** Same pixels as a raw buffer. */
  composite: Uint8ClampedArray;
  /** 3D preview texture: the composite as the GAME draws it (base forced opaque — empty base is black). */
  previewTexture: ImageData | null;
  activeLayer: SkinLayer;
  // settings
  setTool: (tool: Tool) => void;
  setColor: (color: Color) => void;
  setSecondaryColor: (color: Color) => void;
  setBrushSize: (size: BrushSize) => void;
  /** Lighten / Darken / Noise strength, in percent (clamped by the reducer). */
  setShadeStrength: (percent: number) => void;
  setModelType: (type: ModelType) => void;
  togglePartVisibility: (part: BodyPart, layer: Layer) => void;
  setPartVisibility: (part: BodyPart, visible: boolean) => void;
  toggleMirror: () => void;
  toggleGrid: () => void;
  setViewerMode: (mode: 'rotate' | 'paint') => void;
  setLayerViewMode: (mode: LayerViewMode) => void;
  setShapePreview: (preview: ShapePreview | null) => void;
  setHoverCoords: (coords: { x: number; y: number } | null) => void;
  // painting — every view goes through these
  beginStroke: () => void;
  /** Brush centers for the current STROKE tool; pass gap-free points (see linePoints). */
  strokePoints: (points: Point[]) => void;
  endStroke: () => void;
  /** Take back the open stroke: its pixels AND its undo entry (a 3D touch that turned into a pinch). */
  cancelStroke: () => void;
  applyShape: (tool: Tool, x0: number, y0: number, x1: number, y1: number, clip?: Bounds) => void;
  fillAt: (x: number, y: number) => void;
  /** Eyedropper: samples the COMPOSITE (what you see), then switches back to the pencil. */
  pickColor: (x: number, y: number) => void;
  undoAction: () => void;
  redoAction: () => void;
  canUndo: boolean;
  canRedo: boolean;
  // document
  /** Replace the document with an imported skin (one layer). `handoff` = opened with a skin. */
  loadSkin: (skin: ImageData | Uint8ClampedArray, opts?: { source?: 'user' | 'handoff'; name?: string; modelType?: ModelType }) => void;
  /** Call BEFORE starting an async explicit load so a pending autosave restore cannot win. */
  markExplicitLoad: () => void;
  /** The load announced by markExplicitLoad failed: let the autosaved session come back (if unedited). */
  explicitLoadFailed: () => void;
  clearAll: () => void;
  // layers — Phase 2's "import part as a new layer" uses addLayerFromPixels
  addLayer: () => void;
  addLayerFromPixels: (pixels: Uint8ClampedArray, name: string) => void;
  deleteLayer: (id: string) => void;
  duplicateLayer: (id: string) => void;
  moveLayer: (id: string, dir: 1 | -1) => void;
  mergeDown: (id: string) => void;
  flatten: () => void;
  selectLayer: (id: string) => void;
  renameLayer: (id: string, name: string) => void;
  setLayerVisible: (id: string, visible: boolean) => void;
  setLayerLocked: (id: string, locked: boolean) => void;
  /** `gesture` groups a slider drag into one undo step. */
  setLayerOpacity: (id: string, opacity: number, gesture?: number) => void;
  // autosave
  restoredSession: boolean;
  dismissRestored: () => void;
  /** Start a fresh blank document (undoable). */
  startNew: () => void;
  autosaveFailed: boolean;
}

const SkinEditorContext = createContext<SkinEditorContextValue | null>(null);

const HANDOFF_KEY = 'skinEditor_loadBase64';

export function SkinEditorProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(editorReducer, initialState);

  const composite = useMemo(() => compositeDocument(state.doc), [state.doc]);
  const texture = useMemo(
    () => (typeof ImageData === 'undefined' ? null : new ImageData(new Uint8ClampedArray(composite), 64, 64)),
    [composite],
  );
  const previewTexture = useMemo(
    () => (typeof ImageData === 'undefined' ? null : new ImageData(new Uint8ClampedArray(compositeForPreview(composite)), 64, 64)),
    [composite],
  );
  const activeLayer = getActiveLayer(state.doc);

  // ---- autosave: restore on mount (unless the page was opened WITH a skin) ----
  useEffect(() => {
    let explicit = false;
    try {
      explicit = new URLSearchParams(window.location.search).has('skinUrl') || !!window.sessionStorage.getItem(HANDOFF_KEY);
    } catch { /* storage blocked — treat as no handoff */ }
    if (explicit) return;
    const saved = readAutosave();
    if (saved && !isBlankDocument(saved.doc)) dispatch({ type: 'RESTORE', saved });
  }, []);

  // ---- autosave: debounced write + flush on pagehide ----
  const [autosaveFailed, setAutosaveFailed] = useState(false);
  const latestRef = useRef(state);
  const pendingRef = useRef(false);
  useEffect(() => { latestRef.current = state; });

  const flushAutosave = useCallback(() => {
    if (!pendingRef.current) return;
    pendingRef.current = false;
    const s = latestRef.current;
    const ok = writeAutosave({ doc: s.doc, modelType: s.modelType, layerViewMode: s.layerViewMode, savedAt: Date.now() });
    setAutosaveFailed(!ok);
  }, []);

  useEffect(() => {
    if (!state.dirty) return;
    pendingRef.current = true;
    const t = window.setTimeout(flushAutosave, AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [state.dirty, state.doc, state.modelType, state.layerViewMode, flushAutosave]);

  useEffect(() => {
    const onHide = () => flushAutosave();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [flushAutosave]);

  // Only when autosave could NOT store the work is leaving the page a real loss — warn then, only.
  useEffect(() => {
    if (!autosaveFailed || !state.dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [autosaveFailed, state.dirty]);

  // ---- helpers ----
  const setTool = useCallback((tool: Tool) => dispatch({ type: 'SET_TOOL', tool }), []);
  const setColor = useCallback((color: Color) => dispatch({ type: 'SET_COLOR', color }), []);
  const setSecondaryColor = useCallback((color: Color) => dispatch({ type: 'SET_SECONDARY_COLOR', color }), []);
  const setBrushSize = useCallback((size: BrushSize) => dispatch({ type: 'SET_BRUSH_SIZE', size }), []);
  const setShadeStrength = useCallback((strength: number) => dispatch({ type: 'SET_SHADE_STRENGTH', strength }), []);
  const setModelType = useCallback((type: ModelType) => dispatch({ type: 'SET_MODEL_TYPE', modelType: type }), []);
  const togglePartVisibility = useCallback((part: BodyPart, layer: Layer) => dispatch({ type: 'TOGGLE_PART_VISIBILITY', part, layer }), []);
  const setPartVisibility = useCallback((part: BodyPart, visible: boolean) => dispatch({ type: 'SET_PART_VISIBILITY', part, visible }), []);
  const toggleMirror = useCallback(() => dispatch({ type: 'TOGGLE_MIRROR' }), []);
  const toggleGrid = useCallback(() => dispatch({ type: 'TOGGLE_GRID' }), []);
  const setViewerMode = useCallback((mode: 'rotate' | 'paint') => dispatch({ type: 'SET_VIEWER_MODE', mode }), []);
  const setLayerViewMode = useCallback((mode: LayerViewMode) => dispatch({ type: 'SET_LAYER_VIEW_MODE', mode }), []);
  const setShapePreview = useCallback((preview: ShapePreview | null) => dispatch({ type: 'SET_SHAPE_PREVIEW', preview }), []);
  const setHoverCoords = useCallback((coords: { x: number; y: number } | null) => dispatch({ type: 'SET_HOVER_COORDS', coords }), []);

  const beginStroke = useCallback(() => dispatch({ type: 'BEGIN_STROKE' }), []);
  const strokePoints = useCallback((points: Point[]) => { if (points.length) dispatch({ type: 'STROKE', points }); }, []);
  const endStroke = useCallback(() => dispatch({ type: 'END_STROKE' }), []);
  const cancelStroke = useCallback(() => dispatch({ type: 'CANCEL_STROKE' }), []);
  const applyShape = useCallback((tool: Tool, x0: number, y0: number, x1: number, y1: number, clip?: Bounds) =>
    dispatch({ type: 'SHAPE', tool, x0, y0, x1, y1, clip }), []);
  const fillAt = useCallback((x: number, y: number) => dispatch({ type: 'FILL', x, y }), []);
  const undoAction = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redoAction = useCallback(() => dispatch({ type: 'REDO' }), []);

  const compositeRef = useRef(composite);
  useEffect(() => { compositeRef.current = composite; }, [composite]);
  const pickColor = useCallback((x: number, y: number) => {
    const color = readPixel(compositeRef.current, x, y);
    if (color.a > 0) {
      dispatch({ type: 'SET_COLOR', color });
      dispatch({ type: 'SET_TOOL', tool: 'pencil' });
    }
  }, []);

  const loadSkin = useCallback((skin: ImageData | Uint8ClampedArray, opts: { source?: 'user' | 'handoff'; name?: string; modelType?: ModelType } = {}) => {
    const pixels = skin instanceof Uint8ClampedArray ? new Uint8ClampedArray(skin) : new Uint8ClampedArray(skin.data);
    dispatch({ type: 'LOAD_SKIN', pixels, name: opts.name, source: opts.source ?? 'user', modelType: opts.modelType });
  }, []);
  const markExplicitLoad = useCallback(() => dispatch({ type: 'MARK_EXPLICIT_LOAD' }), []);
  // The mount effect skipped the restore because the page was opened WITH a skin; that load failed, so
  // run the restore now. The reducer still refuses it if the user has already edited.
  const explicitLoadFailed = useCallback(() => {
    dispatch({ type: 'EXPLICIT_LOAD_FAILED' });
    const saved = readAutosave();
    if (saved && !isBlankDocument(saved.doc)) dispatch({ type: 'RESTORE', saved });
  }, []);
  const clearAll = useCallback(() => dispatch({ type: 'CLEAR_ALL' }), []);

  const addLayer = useCallback(() => dispatch({ type: 'LAYER_OP', payload: { op: 'add' } }), []);
  const addLayerFromPixels = useCallback((pixels: Uint8ClampedArray, name: string) =>
    dispatch({ type: 'LAYER_OP', payload: { op: 'add', name, pixels } }), []);
  const deleteLayer = useCallback((id: string) => dispatch({ type: 'LAYER_OP', payload: { op: 'delete', id } }), []);
  const duplicateLayer = useCallback((id: string) => dispatch({ type: 'LAYER_OP', payload: { op: 'duplicate', id } }), []);
  const moveLayer = useCallback((id: string, dir: 1 | -1) => dispatch({ type: 'LAYER_OP', payload: { op: 'move', id, dir } }), []);
  const mergeDown = useCallback((id: string) => dispatch({ type: 'LAYER_OP', payload: { op: 'mergeDown', id } }), []);
  const flatten = useCallback(() => dispatch({ type: 'LAYER_OP', payload: { op: 'flatten' } }), []);
  const selectLayer = useCallback((id: string) => dispatch({ type: 'SELECT_LAYER', id }), []);
  const renameLayer = useCallback((id: string, name: string) => dispatch({ type: 'SET_LAYER_PROPS', id, props: { name } }), []);
  const setLayerVisible = useCallback((id: string, visible: boolean) => dispatch({ type: 'SET_LAYER_PROPS', id, props: { visible } }), []);
  const setLayerLocked = useCallback((id: string, locked: boolean) => dispatch({ type: 'SET_LAYER_PROPS', id, props: { locked } }), []);
  const setLayerOpacity = useCallback((id: string, opacity: number, gesture?: number) =>
    dispatch({ type: 'SET_LAYER_PROPS', id, props: { opacity }, coalesceKey: `opacity:${id}:${gesture ?? 0}` }), []);

  const dismissRestored = useCallback(() => dispatch({ type: 'DISMISS_RESTORED' }), []);
  // A blank document AND the default model (a restored slim session used to keep slim), one undo step.
  const startNew = useCallback(() => dispatch({ type: 'START_NEW' }), []);

  // Memoized: every helper is a stable useCallback, so the value changes only when the state or a
  // derived buffer does — not on every render of the provider.
  const canUndo = state.history.past.length > 0;
  const canRedo = state.history.future.length > 0;
  const value = useMemo<SkinEditorContextValue>(() => ({
    state,
    dispatch,
    texture,
    composite,
    previewTexture,
    activeLayer,
    setTool,
    setColor,
    setSecondaryColor,
    setBrushSize,
    setShadeStrength,
    setModelType,
    togglePartVisibility,
    setPartVisibility,
    toggleMirror,
    toggleGrid,
    setViewerMode,
    setLayerViewMode,
    setShapePreview,
    setHoverCoords,
    beginStroke,
    strokePoints,
    endStroke,
    cancelStroke,
    applyShape,
    fillAt,
    pickColor,
    undoAction,
    redoAction,
    canUndo,
    canRedo,
    loadSkin,
    markExplicitLoad,
    explicitLoadFailed,
    clearAll,
    addLayer,
    addLayerFromPixels,
    deleteLayer,
    duplicateLayer,
    moveLayer,
    mergeDown,
    flatten,
    selectLayer,
    renameLayer,
    setLayerVisible,
    setLayerLocked,
    setLayerOpacity,
    restoredSession: state.restored,
    dismissRestored,
    startNew,
    autosaveFailed,
  }), [
    state, canUndo, canRedo, dispatch, texture, composite, previewTexture, activeLayer, setTool, setColor, setSecondaryColor, setBrushSize, setShadeStrength, setModelType, togglePartVisibility, setPartVisibility, toggleMirror, toggleGrid, setViewerMode, setLayerViewMode, setShapePreview, setHoverCoords, beginStroke, strokePoints, endStroke, cancelStroke, applyShape, fillAt, pickColor, undoAction, redoAction, loadSkin, markExplicitLoad, explicitLoadFailed, clearAll, addLayer, addLayerFromPixels, deleteLayer, duplicateLayer, moveLayer, mergeDown, flatten, selectLayer, renameLayer, setLayerVisible, setLayerLocked, setLayerOpacity, dismissRestored, startNew, autosaveFailed,
  ]);

  return (
    <SkinEditorContext.Provider value={value}>
      {children}
    </SkinEditorContext.Provider>
  );
}

// Hook to use the context
export function useSkinEditor() {
  const context = useContext(SkinEditorContext);
  if (!context) {
    throw new Error('useSkinEditor must be used within a SkinEditorProvider');
  }
  return context;
}
